import ReactDOM from "react-dom";
import axios from "axios";
import { getDeviceInfo, getLocationQueryParam } from "../Upshow.js";
import Logger from "../Logger";
import StateService from "../services/StateService";
import { renderInteractiveUI } from "../epg/utils/components/RenderInteractiveUI";
import { getEventDeeplinkId } from "../epg/utils/shared/getEventDeeplink";

const url = new URL(window.location.href);
const WINDOW_INDEX = url.searchParams.get("window_index") ?? "";
const POLLING_TIMEOUT_MS = 10 * 60 * 1000; // 10 mins

const ActivationStatus = {
    INACTIVE: "inactive",
    ACTIVE: "active",
    EXPIRED: "expired",
};

function getActivationStatus(result) {
    if (result.inactive === true) return ActivationStatus.INACTIVE;
    if (result.active === true) return ActivationStatus.ACTIVE;
    if (result.expired === true) return ActivationStatus.EXPIRED;
    return null;
}

async function activationHelper(path) {
    let headers = {};

    const device_info = getDeviceInfo();

    if (device_info.serial) headers["x-serial"] = device_info.serial;
    if (device_info.model) headers["x-model"] = device_info.model;

    return await axios
        .get(process.env.REACT_APP_ACTIVATION_URL + path, { headers })
        .then((results) => results.data ?? {})
        .catch((error) => {
            Logger.error(["activation"], error, { path });
            throw error;
        });
}

export async function fetchActivationCode() {
    try {
        const activationCodeResult = await activationHelper("/code");
        return activationCodeResult.code ?? null;
    } catch (_) {
        return null;
    }
}

export async function checkActivationStatus(code) {
    try {
        const activationResult = await activationHelper("/" + code + "/activate");
        return activationResult;
    } catch (error) {
        if (error.response?.status === 432) {
            return { inactive: true };
        }
        return null;
    }
}

export async function autoActivateWebview(webviewName) {
    let path = `/webview/${encodeURI(webviewName.name)}/activate`;
    if (webviewName.type === "subdomain") {
        path = path + "?type=pwa";
    }

    const activationResult = await activationHelper(path).catch((error) => {
        if (error.response?.status === 432) {
            StateService.showInactiveState();
            StateService.stop();
            return { inactive: true };
        } else if (error.response?.status === 404) {
            let url = new URL(window.location);
            let params = url.searchParams;
            let origin = url.origin;

            switch (webviewName.type) {
                case "hash":
                    break;
                case "params":
                    params.delete("name");
                    break;
                case "subdomain":
                    origin = url.origin.replace(new RegExp("/" + webviewName.name + "."), "/");
                    break;
                default:
                    throw new Error(`Invalid webviewName type ${webviewName.type}`);
            }

            window.location = origin + "?" + params.toString();
            return {};
        }
        return {};
    });

    return activationResult;
}

export function storeAccessToken(token) {
    window.localStorage.setItem("access-token" + WINDOW_INDEX, token);
}

export function getWebviewName() {
    const url = new URL(window.location.href);

    //Hash is the highest priority webview name
    if (url.hash) {
        return { name: url.hash.replace("#", "").toLowerCase(), type: "hash" };
    }

    //Second is ?name parameter
    if (url.searchParams.has("name")) {
        return { name: url.searchParams.get("name"), type: "params" };
    }

    //Finally, if we didn't find a webview name, we'll use the subdomain if present, else we return undefined
    const match = new RegExp(/([\w-+]+)\.(localhost|upshowapp\.com|ui6\.internal\.upshow\.tv|ui6\.upshow\.tv)$/).exec(url.hostname);
    if (match) {
        return { name: match[1], type: "subdomain" };
    }

    return null;
}

export function getAccessToken() {
    // I got a Token in the query string
    const queryParamAccessToken = getLocationQueryParam("api_token");
    if (queryParamAccessToken) return queryParamAccessToken;

    // I have a Token saved in Local Storage.
    const localStorageAccessToken = window.localStorage.getItem("access-token" + WINDOW_INDEX);
    if (localStorageAccessToken) return localStorageAccessToken;

    return null;
}

export function cleanAccessToken() {
    window.localStorage.removeItem("access-token" + WINDOW_INDEX);
}

export function createActivationScreenContainer() {
    let activationContainer = document.getElementById("activation-screen");
    if (!activationContainer) {
        activationContainer = document.createElement("div");
        activationContainer.id = "activation-screen";
        document.body.insertBefore(activationContainer, document.getElementById("root"));
    }
    return activationContainer;
}

export function removeActivationScreenContainer() {
    ReactDOM.unmountComponentAtNode(document.getElementById("activation-screen"));
    const activationContainer = document.getElementById("activation-screen");
    if (activationContainer && document.body.contains(activationContainer)) {
        document.body.removeChild(activationContainer);
    }
}

export async function getActivationCode() {
    try {
        const activationCode = await fetchActivationCode();
        return activationCode;
    } catch (error) {
        Logger.error(["activation"], error);
        return null;
    }
}

export function handleActivationResult(result, { stopPolling, onActivationComplete }) {
    const status = getActivationStatus(result);

    switch (status) {
        case ActivationStatus.INACTIVE:
            StateService.showInactiveState();
            StateService.stop();
            stopPolling();
            break;
        case ActivationStatus.ACTIVE:
            storeAccessToken(result.token);
            const deeplinkEventId = getEventDeeplinkId();
            renderInteractiveUI(deeplinkEventId);
            stopPolling();
            // Upstream callback to let index know
            onActivationComplete(result.token);
            break;
        case ActivationStatus.EXPIRED:
            document.location.reload();
            break;
    }
}

export function checkPollingTimeout(pollingStartTimeRef, setStep, stopPolling, isMultiStep) {
    const pollingStartTime = pollingStartTimeRef.current;
    const currentTime = Date.now();
    const timeElapsed = currentTime - pollingStartTime;
    const hasPollingStarted = !!pollingStartTime;
    const hasTimedOut = timeElapsed > POLLING_TIMEOUT_MS;

    if (hasPollingStarted && hasTimedOut && isMultiStep) {
        stopPolling();
        setStep(1);
        return true;
    }

    return false;
}
